import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, HostListener, Injector, Input, OnDestroy, OnInit, Output, SkipSelf, ViewChild } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ConcretePlan, PlanFactory } from 'src/app/pages/new-billing/models/plan.model';
import { SubscriptionModel } from 'src/app/pages/new-billing/models/subscription.model';
import { UpgradeDowngradeBaseModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-base.model';
import { UpgradeDowngradeOnPaidModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-on-paid.model';
import { UpgradeDowngradeOnTrialModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-on-trial.model';
import type { PlanActionType } from 'src/app/pages/new-billing/types/plan.types';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { PlanStateService } from 'src/app/services/billing/plan-state.service';
import { RecurrenceStateService } from 'src/app/services/billing/recurrence-state.service';
import { UserCountStateService } from 'src/app/services/billing/user-count-state.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { PlanSelectionService } from 'src/app/services/plan-selection/plan-selection.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { TrackingContext } from 'src/app/services/tracking/context';
import { PlanCardClickEvent } from '../plan-card/plan-card.component';

// Types for better type safety
interface FeatureItem {
  name: string;
  value?: any;
  label?: string;
}

interface FeatureCategory {
  category?: string;
  items: FeatureItem[];
}

interface PlanFeature {
  category: string;
  name: string;
  value: any;
}

@UntilDestroy()
@Component({
  selector: 'app-plan-comparison',
  templateUrl: './plan-comparison.component.html',
  styleUrls: ['./plan-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PlanComparisonComponent implements OnDestroy, AfterViewInit, OnInit {
  // Constants
  private static readonly defaultExpandedCategories = new Set(['Platform Features']);
  private static readonly scrollDelayMs = 300;
  private static readonly checkIconPath = '/assets/images/billing/pricing-plan-card/check-icon.svg';

  // Input/Output properties
  @Input() payRecurrence: PricePeriodicity = 'year';
  @Output() payRecurrenceChange = new EventEmitter<PricePeriodicity>();
  @Output() planChanged = new EventEmitter<void>();

  // Current plan with setter logic
  @Input()
  get currentPlan(): ConcretePlan {
    return this._currentPlan;
  }

  set currentPlan(value: ConcretePlan) {
    this._currentPlan = value;
    this.initializePlansAndModel();
  }

  // Public properties
  plans: ConcretePlan[] = [];
  expandedCategories: Set<string> = new Set(PlanComparisonComponent.defaultExpandedCategories);
  isPanelExpanded = false;
  // User count shared with calculator
  userCount: number | null = 1;

  // Private properties
  private _currentPlan: ConcretePlan;
  private upgradeDowngradeModel: UpgradeDowngradeBaseModel;
  private readonly destroy$ = new Subject<void>();
  private iconRegistered = false;
  protected isStandardDefault = false;

  // Host bindings
  @HostBinding('class.panel-expanded')
  get isPanelExpandedClass(): boolean {
    return this.isPanelExpanded;
  }

  @HostBinding('style.scroll-behavior')
  scrollBehavior = 'smooth';

  @HostBinding('style.scroll-margin-top')
  get dynamicScrollMargin(): string {
    return this.isPanelExpanded ? '100vh' : '0px';
  }

  // View references
  @ViewChild('comparisonHeader', { static: false })
  comparisonHeader!: ElementRef;

  @ViewChild('planHeadersTable', { static: false })
  planHeadersTable!: ElementRef;

  // Sticky header properties
  isHeaderSticky = false;
  private stickyThreshold = 0;
  private scrollTimeout: any;
  private lastScrollTop = 0;
  private readonly scrollDebounceMs = 16; // ~60fps
  private headerHeight = 0;
  private stickyHeaderElement: HTMLElement | null = null;
  private resizeTimeout: any = null;
  private initializeTimeout: any = null;
  private scrollToHeaderTimeout: any = null;

  // Computed properties
  get currentPlanFeatures(): FeatureCategory[] {
    return this.currentPlan?.featuresLong || [];
  }

  get subscription(): SubscriptionModel {
    return this.currentPlan?.subscription;
  }

  get shouldShowSocialProofOnBillingPage(): boolean {
    const feature = this.growthBookService.getFeatureValue<ExperimentProps>('billing-page-social-proof-exp');
    return feature?.variationid === 'B';
  }

  constructor(
    private readonly growthBookService: GrowthBookService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly planStateService: PlanStateService,
    private readonly planSelectionService: PlanSelectionService,
    private readonly recurrenceStateService: RecurrenceStateService,
    private readonly userCountState: UserCountStateService,
    @SkipSelf() private readonly trackingCtx: TrackingContext,
    private readonly segment: SegmentService,
    private readonly injector: Injector,
  ) {
    this.registerCustomIcon();
  }

  ngOnInit(): void {
    // Subscribe to shared state changes
    this.recurrenceStateService.payRecurrence$
      .pipe(takeUntil(this.destroy$))
      .subscribe(recurrence => {
        if (this.payRecurrence !== recurrence) {
          this.payRecurrence = recurrence;
          this.payRecurrenceChange.emit(recurrence);
        }
      });

    // Subscribe to user count changes shared from the billing calculator
    this.userCountState.userCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.userCount = count;
      });

    this.growthBookService.getFeature<ExperimentProps>('standard-default-pricing-page')
      .pipe(
        map(value => value?.variationid === 'B'),
        takeUntil(this.destroy$),
      )
      .subscribe(isB => {
        this.isStandardDefault = isB;
      });
  }

  ngOnDestroy() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.initializeTimeout) {
      clearTimeout(this.initializeTimeout);
    }
    if (this.scrollToHeaderTimeout) {
      clearTimeout(this.scrollToHeaderTimeout);
    }
    // Reset sticky header state
    this.isHeaderSticky = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.calculateStickyThreshold();
    this.calculateHeaderHeight();
    this.initializeStickyHeader();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    // Debounce scroll events for better performance
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.updateStickyState();
    }, this.scrollDebounceMs);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.calculateStickyThreshold();
    this.calculateHeaderHeight();
    // Recalculate sticky state on resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.updateStickyState();
    }, 100);
  }

  private calculateStickyThreshold(): void {
    if (this.comparisonHeader?.nativeElement) {
      const headerRect = this.comparisonHeader.nativeElement.getBoundingClientRect();
      this.stickyThreshold = headerRect.bottom;
    }
  }

  private calculateHeaderHeight(): void {
    if (this.planHeadersTable?.nativeElement) {
      const headerElement = this.planHeadersTable.nativeElement.querySelector('thead');
      if (headerElement) {
        this.headerHeight = headerElement.offsetHeight;
      }
    }
  }

  private initializeStickyHeader(): void {
    // Get reference to sticky header element after view init
    if (this.initializeTimeout) {
      clearTimeout(this.initializeTimeout);
    }
    this.initializeTimeout = setTimeout(() => {
      this.stickyHeaderElement = document.querySelector('.sticky-header-container');
    }, 100);
  }

  private updateStickyState(): void {
    if (!this.planHeadersTable?.nativeElement) {
      return;
    }

    const rect = this.planHeadersTable.nativeElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate when the table header should become sticky
    // Use dynamic header height and add a small buffer for smoother transition
    const buffer = 10; // 10px buffer for smoother transition
    const shouldBeSticky = rect.top <= -(this.headerHeight + buffer) && rect.bottom >= this.headerHeight;

    // Only update state if it actually changed to prevent unnecessary re-renders
    if (this.isHeaderSticky !== shouldBeSticky) {
      this.isHeaderSticky = shouldBeSticky;

      // Use requestAnimationFrame for smooth visual updates
      requestAnimationFrame(() => {
        if (this.stickyHeaderElement) {
          if (shouldBeSticky) {
            // When becoming sticky, ensure smooth appearance
            this.stickyHeaderElement.style.transform = 'translateX(-50%) translateY(0)';
            this.stickyHeaderElement.style.opacity = '1';
            this.stickyHeaderElement.style.pointerEvents = 'auto';
          } else {
            // When becoming unsticky, ensure smooth disappearance
            this.stickyHeaderElement.style.transform = 'translateX(-50%) translateY(-100%)';
            this.stickyHeaderElement.style.opacity = '0';
            this.stickyHeaderElement.style.pointerEvents = 'none';
          }
        }
      });
    }
  }

  // Category management methods
  toggleCategory(categoryGroup: string): void {
    if (this.expandedCategories.has(categoryGroup)) {
      this.expandedCategories.delete(categoryGroup);
    } else {
      this.expandedCategories.add(categoryGroup);
    }
  }

  isCategoryExpanded(categoryGroup: string): boolean {
    return this.expandedCategories.has(categoryGroup);
  }

  // Feature value retrieval methods
  getFeatureLabel(plan: any, categoryGroup: string, featureName: string): string {
    const feature = this.findFeatureInPlan(plan, categoryGroup, featureName);
    return feature?.value ?? 'N/A';
  }

  getFeatureValue(plan: any, item: FeatureItem): any {
    const category = this.findCategoryWithFeature(plan, item.name);

    if (category) {
      const specificFeature = category.items.find((feature: FeatureItem) => feature.name === item.name);
      return specificFeature?.value ?? specificFeature?.label ?? 'N/A';
    }

    return 'N/A';
  }

  // Recurrence handling
  handleRecurrenceButtonClick(recurrence: PricePeriodicity): void {
    // Update the shared state service
    this.recurrenceStateService.setPayRecurrence(recurrence);

    // Also emit locally for backward compatibility
    this.payRecurrenceChange.emit(recurrence);
    this.payRecurrence = recurrence;
  }

  // Plan state methods
  isRecommendedPlan(plan: ConcretePlan): boolean {
    return this.isStandardDefault
      ? plan.productId === 'standard'
      : plan.productId === 'premium';
  }

  isCurrentPlan(plan: ConcretePlan): boolean {
    return this.planStateService.isCurrentPlan(plan, this.currentPlan, this.payRecurrence);
  }

  getDeterminePlanState(plan: ConcretePlan): () => PlanActionType {
    return this.planStateService.createPlanStateGetter(plan, this.currentPlan, this.payRecurrence);
  }

  // Formatting methods
  getNextPaymentDateFormatted(): string {
    return this.planStateService.formatNextPaymentDate(this.currentPlan?.paymentModel?.nextPaymentDate);
  }

  getTrackingData = (action: 'upgrade' | 'downgrade'): any => {
    return this.planStateService.getTrackingData(action, this.subscription);
  };

  // Plan change handling
  handlePlanCardBtnClick(plan: ConcretePlan, isV2: boolean = false): void {
    const clonedPlan = this.createPlanWithRecurrence(plan);

    this.handlePlanChange({
      plan: clonedPlan,
      actionType: this.getDeterminePlanState(plan)(),
      isV2,
    });
  }

  // Panel state management
  onPanelOpened(): void {
    this.trackSeeAllFeaturesAndComparePlansClick();
    this.isPanelExpanded = true;
    this.scrollToComparisonHeader();
  }

  onPanelClosed(): void {
    this.isPanelExpanded = false;
  }

  private trackSeeAllFeaturesAndComparePlansClick() {
    this.segment.trackInAppEvents('Link Click',
      {
        linklocation: 'Plan comparison',
        linkname: `See all features and compare plans`,
        pagename: 'billing page',
        linktype: 'accordion',
      },
    );
  }

  // Private helper methods
  private initializePlansAndModel(): void {
    this.plans = PlanFactory.createCardPlans(this._currentPlan, this.growthBookService)
      .map(cardPlan => cardPlan.plan);

    this.upgradeDowngradeModel = this.createUpgradeDowngradeModel();
  }

  private createUpgradeDowngradeModel(): UpgradeDowngradeBaseModel {
    return this._currentPlan.subscription.status === 'trial'
      ? new UpgradeDowngradeOnTrialModel(this.injector)
      : new UpgradeDowngradeOnPaidModel(this.injector);
  }

  private findFeatureInPlan(plan: any, categoryGroup: string, featureName: string): PlanFeature | undefined {
    return plan.features?.find((f: PlanFeature) =>
      f.category === categoryGroup && f.name === featureName,
    );
  }

  private findCategoryWithFeature(plan: any, featureName: string): FeatureCategory | undefined {
    return plan.featuresLong?.find((cat: FeatureCategory) =>
      cat.items.some((feature: FeatureItem) => feature.name === featureName),
    );
  }

  private createPlanWithRecurrence(plan: ConcretePlan): ConcretePlan {
    const clonedPlan = plan.clone();
    clonedPlan.switchPlanRecurrence(this.payRecurrence);
    return clonedPlan;
  }

  private handlePlanChange({ plan, actionType, isV2 }: PlanCardClickEvent): void {
    if (!this.currentPlan.paymentModel?.cardLast4) {
      this.planSelectionService.handlePlanSelection(
        plan, this.shouldShowSocialProofOnBillingPage,
        this.trackingCtx.context,
        isV2,
      );
      return;
    }
    this.planStateService.handlePlanChange(
      { plan, actionType },
      this.upgradeDowngradeModel,
      undefined,
      isV2,
    );
  }

  private scrollToComparisonHeader(): void {
    if (this.scrollToHeaderTimeout) {
      clearTimeout(this.scrollToHeaderTimeout);
    }
    this.scrollToHeaderTimeout = setTimeout(() => {
      if (this.comparisonHeader?.nativeElement) {
        this.comparisonHeader.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, PlanComparisonComponent.scrollDelayMs);
  }

  private registerCustomIcon(): void {
    // Only register the icon once to prevent memory leaks
    if (!this.iconRegistered) {
      this.matIconRegistry.addSvgIcon(
        'check-icon',
        this.domSanitizer.bypassSecurityTrustResourceUrl(PlanComparisonComponent.checkIconPath),
      );
      this.iconRegistered = true;
    }
  }
}

